import React from 'react';
import { Instagram, Linkedin } from 'lucide-react';
import { Link } from 'react-router-dom';

// Custom X (Twitter) icon
const XIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

// Custom Discord icon
const DiscordIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    legal: [
      { name: 'Termos de Uso', href: '/termos' },
      { name: 'Política de Privacidade', href: '/termos' },
      { name: 'Termos Legais', href: '/termos' }
    ],
    support: [
      { name: 'Contato', href: '/contato' },
      { name: 'Reportar Problema', href: '/reportar-problema' }
    ],
    company: [
      { name: 'Sobre Nós', href: '/sobre' },
      { name: 'Carreiras', href: '/carreiras' },
      { name: 'Blog', href: '/blog' },
      { name: 'Fique por dentro', href: '/newsletter' },
      { name: 'Compartilhe sua ideia', href: '/sugestoes' }
    ]
  };

  return (
    <footer className="bg-dark-primary border-t border-purple-primary/20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Brand Section */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_gameodys-hub/artifacts/fbqbhqqa_WhatsApp_Image_2025-09-08_at_21.11.32-removebg-preview.png" 
                alt="Gameodys Logo" 
                className="h-12 w-12 object-contain brightness-0 invert"
              />
              <span className="text-2xl font-bold font-archive text-white">GAMEODYS</span>
            </div>
            <p className="text-gray-400 font-hero text-sm mb-6">
              Transformando habilidade em oportunidade. Jogue, compete, ganhe.
            </p>
            <div className="flex gap-3">
              <a href="https://www.instagram.com/gameodys/" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-dark-secondary text-gray-400 hover:text-green-primary hover:bg-green-primary/10 transition-all duration-300">
                <Instagram size={18} />
              </a>
              <a href="#" className="p-2 rounded-full bg-dark-secondary text-gray-400 hover:text-green-primary hover:bg-green-primary/10 transition-all duration-300">
                <XIcon />
              </a>
              <a href="#" className="p-2 rounded-full bg-dark-secondary text-gray-400 hover:text-green-primary hover:bg-green-primary/10 transition-all duration-300">
                <Linkedin size={18} />
              </a>
              <a href="https://discord.gg/Rdmc5YJt" target="_blank" rel="noopener noreferrer" className="p-2 rounded-full bg-dark-secondary text-gray-400 hover:text-green-primary hover:bg-green-primary/10 transition-all duration-300">
                <DiscordIcon />
              </a>
            </div>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-white font-archive font-semibold mb-4">Legal</h4>
            <ul className="space-y-2">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  {link.href.startsWith('/') ? (
                    <Link to={link.href} className="text-gray-400 hover:text-green-primary transition-colors duration-300 text-sm font-hero">
                      {link.name}
                    </Link>
                  ) : (
                    <a href={link.href} className="text-gray-400 hover:text-green-primary transition-colors duration-300 text-sm font-hero">
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 className="text-white font-archive font-semibold mb-4">Suporte</h4>
            <ul className="space-y-2">
              {footerLinks.support.map((link) => (
                <li key={link.name}>
                  {link.href.startsWith('/') ? (
                    <Link to={link.href} className="text-gray-400 hover:text-green-primary transition-colors duration-300 text-sm font-hero">
                      {link.name}
                    </Link>
                  ) : (
                    <a href={link.href} className="text-gray-400 hover:text-green-primary transition-colors duration-300 text-sm font-hero">
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-white font-archive font-semibold mb-4">Empresa</h4>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  {link.href.startsWith('/') ? (
                    <Link to={link.href} className="text-gray-400 hover:text-green-primary transition-colors duration-300 text-sm font-hero">
                      {link.name}
                    </Link>
                  ) : (
                    <a href={link.href} className="text-gray-400 hover:text-green-primary transition-colors duration-300 text-sm font-hero">
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-purple-primary/20">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-center md:text-left">
              <p className="text-gray-500 text-sm font-hero">
                © {currentYear} Gameodys. Todos os direitos reservados.
              </p>
              <p className="text-gray-600 text-xs font-hero mt-1">
                CNPJ: 67.814.779/0001-50
              </p>
            </div>
            <p className="text-gray-500 text-sm font-hero">
              Jogue com responsabilidade. +18
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
